import * as React from 'react';

export interface SparklineProps {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  animate?: boolean;
  className?: string;
  /**
   * Draws the final segment (and the end dot) dashed, for a last data point that is still
   * accumulating (e.g. today, a partial day) — so it reads as "still filling in", not as a real
   * drop. Default false: every existing caller is unaffected. Lead origins v2's drill-down table
   * is the first caller (orchestrator front-end check, 2026-10-02: "dashed last sparkline
   * segment... so it doesn't read as a collapse").
   */
  dashLastSegment?: boolean;
}

export function Sparkline({
  data,
  color = 'var(--chart-1)',
  width = 120,
  height = 36,
  fill = true,
  animate = true,
  className,
  dashLastSegment = false,
}: SparklineProps) {
  const id = React.useId();
  if (!data.length) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data.map<[number, number]>((v, i) => [
    i * step,
    height - ((v - min) / span) * (height - 6) - 3,
  ]);
  const toPath = (pts: [number, number][]) =>
    pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const path = toPath(points);
  const fillPath = fill ? `${path} L${width},${height} L0,${height} Z` : null;
  const last = points[points.length - 1]!;

  // Split the line at the second-to-last point so only the segment INTO the partial point dashes
  // — every earlier (complete) segment stays solid.
  const splitPartial = dashLastSegment && points.length > 1;
  const solidPoints = splitPartial ? points.slice(0, -1) : points;
  const solidPath = splitPartial ? toPath(solidPoints) : path;
  const partialSegmentPath = splitPartial ? toPath([points[points.length - 2]!, last]) : null;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      className={className}
      aria-hidden
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          {fillPath && <path d={fillPath} fill={`url(#${id})`} />}
        </>
      )}
      <path
        d={solidPath}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={animate ? 'spark-path' : undefined}
      />
      {partialSegmentPath ? (
        <path
          d={partialSegmentPath}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="2.5 2"
          strokeOpacity={0.6}
        />
      ) : null}
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} fillOpacity={splitPartial ? 0.6 : 1} />
    </svg>
  );
}
